import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import CorrespondenceDrawer from "messaging/correspondence/_views/correspondence-drawer";
import ProposalCorrespondence from "work/proposals/comments/_view/proposal-correspondence";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { useProposalPageCanSeatSidePanel } from "work/proposals/_views/_shared/proposal-page-panels";
import Proposal from "work/proposals/proposal";
import type { ForumRequest } from "messaging/conversations/conversation-links";

/**
 * What the pane calls itself: the negotiation's conversation, and nothing else (#5398).
 *
 * "Comments", the word for what the pane holds (#5818). #5565 named it "Correspondence" to match the
 * work agreement's chat panel, which keeps that name: the chat there is not a proposal's comments.
 */
export const proposalCommentsTitle = "Comments";

/** The minimize button's tooltip, in the pane's own word. */
export const proposalCommentsMinimizeLabel = "Minimize comments panel";

type CommentsDrawerProps = {
  proposal: Proposal | null | undefined;
  // The comment layer reads/writes Messaging Forums keyed by the durable negotiationId. The page
  // supplies a comment-scoped proposal (id = negotiationId) plus its populated threads.
  commentThreads: CommentThread[] | undefined;
  isOpen: boolean;
  isSaving?: boolean;
  readOnly?: boolean;
  showMarkAllRead?: boolean;
  title?: string;
  /**
   * The negotiation this one revises, when it revises one: the forums its comments are filed under.
   *
   * A revision of hired work opens on terms both parties already agreed, and everything said while
   * they were agreed is the context for changing them. Reading it meant leaving the revision, finding
   * the archived proposal and reading it there. Absent for a first-time offer, and absent for a
   * reader the prior negotiation does not admit.
   */
  carriedOverNegotiationId?: string;
  /** The proposal revises a negotiation the reader is refused, so the pane says it is not available. */
  carriedOverRefused?: boolean;
  /**
   * The comment to scroll the list to — the oldest unread one, when "Prev / Next" arrives at the
   * unread comments. The carried-over negotiation above is never the target: its comments are not
   * this negotiation's unread count.
   */
  commentToScrollTo?: string;
  /** The conversation a "Prev / Next" stop arrived at: the pane opens its tab (#5824). */
  conversationToOpen?: { readonly isExternal: boolean };
  /** The conversation the page's URL asked the pane to open on (#5826). */
  forumRequest?: ForumRequest;
  onOpen?: () => void;
  onClose: () => void;
};

/**
 * The proposal's Comments pane, in the drawer the work agreement's opens in.
 *
 * On a window with room for it, the drawer sits beside the proposal, in the column the page reserves
 * for it. On a window without room -- a phone, a narrow laptop window -- the page reserves no column,
 * and the drawer is painted over the page instead, as the work agreement's is. It used to stay beside
 * the page at every width, and a page that reserved it no room painted its own content over it, so on
 * a phone the pane could not be seen at all.
 */
export default function CommentsDrawer(props: Readonly<CommentsDrawerProps>) {
  const { proposal, commentThreads, isOpen, title, carriedOverNegotiationId, commentToScrollTo, conversationToOpen, onClose } =
    props;
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();

  return (
    <CorrespondenceDrawer
      isOpen={isOpen}
      isOverlay={!canSeatSidePanel}
      width={SIDE_PANEL_WIDTH}
      inReservedColumn
      title={title || proposalCommentsTitle}
      minimizeLabel={proposalCommentsMinimizeLabel}
      data-testid="proposal-correspondence-drawer"
      onClose={onClose}
    >
      {proposal?.id && (
        <ProposalCorrespondence
          proposal={proposal}
          commentThreads={commentThreads ?? []}
          isOpen={isOpen}
          isSaving={props.isSaving}
          readOnly={props.readOnly}
          showMarkAllRead={props.showMarkAllRead}
          carriedOverNegotiationId={carriedOverNegotiationId}
          carriedOverRefused={props.carriedOverRefused}
          commentToScrollTo={commentToScrollTo}
          conversationToOpen={conversationToOpen}
          forumRequest={props.forumRequest}
        />
      )}
    </CorrespondenceDrawer>
  );
}
